import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const SECTIONS = [
  {
    id: 'commitment',
    title: '1. Our Commitment',
    body: [
      'Aloha Amino Research Inc. is committed to ensuring digital accessibility for people with disabilities. We want everyone who visits alohaamino.com to be able to browse our catalog, place an order, and manage their account, regardless of the assistive technology they use.',
      'We are continually improving the user experience for everyone and applying the relevant accessibility standards to achieve this.',
    ],
  },
  {
    id: 'standard',
    title: '2. Conformance Standard',
    body: [
      'We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, published by the World Wide Web Consortium (W3C). These guidelines explain how to make web content more accessible for people with a wide range of disabilities, including visual, auditory, physical, speech, cognitive, and neurological disabilities.',
      'These same guidelines are the benchmark U.S. courts most commonly apply when evaluating website accessibility under the Americans with Disabilities Act (ADA).',
    ],
  },
  {
    id: 'measures',
    title: '3. What We Have Done',
    body: [
      'Keyboard navigation: every interactive control can be reached and operated with a keyboard alone, and a visible focus indicator shows where you are on the page.',
      '"Skip to main content" link: keyboard and screen-reader users can jump straight past the navigation to the page content.',
      'Semantic structure: pages use proper headings, landmarks (navigation, main, footer), and labels so screen readers can describe them accurately.',
      'Forms: inputs have associated labels, and errors are announced in text rather than by color alone.',
      'Reduced motion: if you have enabled "reduce motion" in your operating system, we minimize animations and transitions site-wide.',
      'Images and icons: meaningful images include text alternatives, and decorative graphics are hidden from assistive technology.',
    ],
  },
  {
    id: 'ongoing',
    title: '4. Ongoing Effort',
    body: [
      'Accessibility is an ongoing effort, not a one-time project. We test new features against these standards before release, and we review the site periodically. Some third-party content or older pages may not yet be fully conformant; where we find gaps, we prioritize and fix them.',
    ],
  },
  {
    id: 'feedback',
    title: '5. Report a Barrier',
    body: [
      'If you encounter any barrier to using this website, or need information on this site in a different accessible format, we want to hear from you. Please email accessibility@alohaamino.com and describe the page and the problem. We aim to respond within 5 business days and will work with you to provide the information, item, or transaction you need through an accessible method.',
    ],
  },
];

export default function AccessibilityPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <section className="bg-tea-700 text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Accessibility Statement</h1>
          <p className="mt-3 text-tea-200">Last updated July 2026 · Aloha Amino Research Inc.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 flex gap-12">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-56 shrink-0" aria-label="Accessibility statement contents">
          <div className="sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-sm text-gray-500 hover:text-tea-700 transition-colors py-1 leading-tight"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main id="main-content" tabIndex={-1} className="flex-1 min-w-0">
          <div className="flex flex-col gap-10">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{s.title}</h2>
                {s.body.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-3">{para}</p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-4">
              Accessibility feedback? Email <a href="mailto:accessibility@alohaamino.com" className="text-tea-700 underline">accessibility@alohaamino.com</a>
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy" className="text-tea-700 font-medium hover:underline text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-tea-700 font-medium hover:underline text-sm">Terms of Service</Link>
              <Link to="/compliance" className="text-tea-700 font-medium hover:underline text-sm">Research Use Disclosure</Link>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
